import { EnvxResult, EnvxSchema } from "../types";

interface ValidationError {
  key: string;
  message: string;
  severity: "error" | "warning";
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

function checkRequired(key: string, hasKey: boolean): ValidationError | null {
  if (!hasKey) {
    return {
      key,
      message: `Missing required environment variable "${key}".`,
      severity: "error",
    };
  }
  return null;
}

function checkDeprecated(
  key: string,
  varSchema: any,
  hasKey: boolean
): ValidationError | null {
  if (varSchema.deprecated && hasKey) {
    return {
      key,
      message: `Environment variable "${key}" is deprecated. ${varSchema.deprecated}`,
      severity: "warning",
    };
  }
  return null;
}

function checkType(
  key: string,
  value: any,
  varSchema: any
): ValidationError | null {
  if (value === undefined || value === null) return null;

  switch (varSchema.type) {
    case "number":
      if (typeof value === "string" && isNaN(Number(value))) {
        return {
          key,
          message: `Environment variable "${key}" should be a number.`,
          severity: "error",
        };
      }
      break;
    case "boolean":
      if (
        value !== true &&
        value !== false &&
        value !== "true" &&
        value !== "false"
      ) {
        return {
          key,
          message: `Environment variable "${key}" should be a boolean ("true" or "false").`,
          severity: "error",
        };
      }
      break;
    case "enum":
      if (varSchema.values && !varSchema.values.includes(String(value))) {
        return {
          key,
          message: `Environment variable "${key}" has invalid value "${value}". Expected one of: ${varSchema.values.join(
            ", "
          )}.`,
          severity: "error",
        };
      }
      break;
    default:
      break;
  }
  return null;
}

export function cliValidateEnvx(
  env: EnvxResult,
  schema: EnvxSchema | undefined
): ValidationResult {
  const errors: ValidationError[] = [];
  if (!schema) return { valid: true, errors: [] };

  const envMap = new Map(Object.entries(env));

  // check required, deprecated, type
  for (const [key, varSchema] of Object.entries(schema)) {
    const hasKey = envMap.has(key);
    const value = envMap.get(key);

    const checks = [
      checkRequired(key, hasKey),
      checkDeprecated(key, varSchema, hasKey),
      checkType(key, value, varSchema),
    ];

    checks.forEach((err) => {
      if (err) errors.push(err);
    });
  }

  for (const key of Object.keys(env)) {
    if (!schema[key]) {
      errors.push({
        key,
        message: `Environment variable "${key}" is not defined in the schema.`,
        severity: "warning",
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
